/**
 * @description This function provides a callback function that, when called with a knowledge id,
 *  deletes that knowledge entry and reloads the knowledge list.
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import decodeJson from '@seccon/shared/utils/decodeJson';
import { ToastContext } from '@seccon/core/contexts/toast';
import { ConfirmDialogContext } from '@seccon/core/contexts/confirm';

const useDeleteKnowledge = ({ knowledgeMap, selectedCategory, setSelectedCategory }) => {
  const toastApi = React.useContext(ToastContext);
  const confirm = React.useContext(ConfirmDialogContext);
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (id) => (
      fetch('/KnowledgeCenter/delete', { method: 'DELETE', body: JSON.stringify({ id }) })
        .then(decodeJson)
    ),
    // On success, notify the user and clear the knowledge data so it can be re-queried
    onSuccess: () => {
      toastApi.success('Knowledge center entry deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['fetchKnowledge'] });

      if (knowledgeMap[selectedCategory].length === 1) {
        const newSelectedCategory = Object.keys(knowledgeMap).filter((id) => id !== selectedCategory)[0] ?? null;
        setSelectedCategory(newSelectedCategory);
      }
    },
    // On failure, notify the user
    onError: ({ body }) => {
      toastApi.error(body ? `Error: ${ body }` : 'Unable to delete knowledge center entry. Please try again.');
    }
  })

  /**
   * Handles deletion events by first confirming the action before calling the mutate function
   * @param {string} id - the id of the knowledge entry to be deleted
   */
  function handleDelete (id) {
    confirm('Delete Knowledge Center Entry', 'Are you should you want delete this knowledge center entry?', () => mutateAsync(id));
  }

  return handleDelete;
};

export default useDeleteKnowledge;
